import { clsx } from 'clsx';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { ReactNode } from 'react';
import { Button } from './Button';
import { EmptyState } from './EmptyState';
import { TableSkeleton } from './Skeleton';

export interface Column<T> {
  key: string;
  header: string;
  /** Cell renderer. Falls back to the raw value. */
  render?: (row: T) => ReactNode;
  className?: string;
  headerClassName?: string;
  sortable?: boolean;
}

/**
 * Responsive table.
 * Desktop: real <table>. Mobile: each row becomes a stacked card, so wide
 * tables never force horizontal scrolling on a phone.
 */
export function DataTable<T extends { id: string }>({ columns, rows, loading, empty, onRowClick, sort, onSort, footer }: {
  columns: Column<T>[];
  rows: T[];
  loading?: boolean;
  empty?: ReactNode;
  onRowClick?: (row: T) => void;
  sort?: { key: string; dir: 'asc' | 'desc' };
  onSort?: (key: string) => void;
  footer?: ReactNode;
}) {
  if (loading) return <TableSkeleton rows={6} cols={Math.min(columns.length, 4)} />;
  if (!rows.length) return <>{empty ?? <EmptyState title="Nothing here yet" />}</>;

  return (
    <div className="w-full">
      {/* Desktop */}
      <div className="hidden md:block">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-line text-start">
              {columns.map((c) => (
                <th
                  key={c.key}
                  scope="col"
                  className={clsx(
                    'px-4 py-2.5 text-start text-xs font-semibold uppercase tracking-wide text-ink-muted',
                    c.sortable && onSort && 'cursor-pointer select-none hover:text-ink',
                    c.headerClassName,
                  )}
                  onClick={c.sortable && onSort ? () => onSort(c.key) : undefined}
                  aria-sort={sort?.key === c.key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                >
                  <span className="inline-flex items-center gap-1">
                    {c.header}
                    {sort?.key === c.key && <span aria-hidden>{sort.dir === 'asc' ? '↑' : '↓'}</span>}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.id}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={clsx(
                  'border-b border-line last:border-0',
                  onRowClick && 'cursor-pointer transition-colors hover:bg-surface-2',
                )}
              >
                {columns.map((c) => (
                  <td key={c.key} className={clsx('px-4 py-3 align-middle text-ink', c.className)}>
                    {c.render ? c.render(row) : String((row as Record<string, unknown>)[c.key] ?? '—')}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile: stacked cards */}
      <ul className="divide-y divide-line md:hidden">
        {rows.map((row) => (
          <li
            key={row.id}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
            className={clsx('space-y-1.5 p-4', onRowClick && 'cursor-pointer active:bg-surface-2')}
          >
            {columns.map((c) => (
              <div key={c.key} className="flex items-start justify-between gap-4">
                <span className="shrink-0 text-xs font-medium uppercase tracking-wide text-ink-faint">{c.header}</span>
                <span className={clsx('text-end text-sm text-ink', c.className)}>
                  {c.render ? c.render(row) : String((row as Record<string, unknown>)[c.key] ?? '—')}
                </span>
              </div>
            ))}
          </li>
        ))}
      </ul>

      {footer}
    </div>
  );
}

export function Pagination({ page, pages, total, onChange }: {
  page: number; pages: number; total: number; onChange: (page: number) => void;
}) {
  if (pages <= 1) return <div className="px-4 py-2 text-xs text-ink-faint">{total} record{total === 1 ? '' : 's'}</div>;
  return (
    <div className="flex items-center justify-between gap-3 border-t border-line px-4 py-2">
      <p className="text-xs text-ink-muted">
        Page <span className="font-medium text-ink">{page}</span> of {pages} · {total} total
      </p>
      <div className="flex gap-1">
        <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => onChange(page - 1)} aria-label="Previous page">
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Button variant="secondary" size="sm" disabled={page >= pages} onClick={() => onChange(page + 1)} aria-label="Next page">
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
